/**
 * Local cache of an organization's Supabase credentials (URL + anon key).
 *
 * Why: on a cold start the org credentials normally come from the central
 * registry over the network. Offline, that fetch fails before the org client —
 * and therefore the locally stored auth session — can ever be used, which
 * kicks the user to the login screen. Caching the credentials after each
 * successful registry fetch lets the app reconstruct the org client entirely
 * offline. Anon keys are public by design (they ship in every client bundle),
 * so persisting one is not a secret-leak concern; data access is still
 * enforced by the org's RLS and the user's JWT.
 */

import type { Session } from '@supabase/supabase-js';
import { STORAGE_KEYS } from '@/services/supabase/config';

export interface CachedOrganizationCredentials {
  supabaseUrl: string;
  supabaseAnonKey: string;
}

const keyForSlug = (slug: string): string =>
  `${STORAGE_KEYS.organizationCredentialsPrefix}:${slug}`;

export const cacheOrganizationCredentials = (
  slug: string,
  credentials: CachedOrganizationCredentials
): void => {
  try {
    localStorage.setItem(keyForSlug(slug), JSON.stringify(credentials));
  } catch {
    // Storage full/unavailable: the cache is an optimization, never fatal.
  }
};

export const getCachedOrganizationCredentials = (
  slug: string
): CachedOrganizationCredentials | null => {
  try {
    const raw = localStorage.getItem(keyForSlug(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedOrganizationCredentials>;
    if (
      typeof parsed?.supabaseUrl === 'string' &&
      typeof parsed?.supabaseAnonKey === 'string'
    ) {
      return { supabaseUrl: parsed.supabaseUrl, supabaseAnonKey: parsed.supabaseAnonKey };
    }
    return null;
  } catch {
    return null;
  }
};

export const clearCachedOrganizationCredentials = (slug: string): void => {
  try {
    localStorage.removeItem(keyForSlug(slug));
  } catch {
    // ignore
  }
};

/**
 * Default supabase-js auth storage key for a project URL:
 * `sb-<project-ref>-auth-token`, where the project ref is the URL subdomain.
 * Org clients are created without a custom `storageKey`, so this matches
 * where gotrue persists the session.
 */
export const getAuthStorageKeyForUrl = (supabaseUrl: string): string | null => {
  try {
    const ref = new URL(supabaseUrl).hostname.split('.')[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch {
    return null;
  }
};

/**
 * Reads the session gotrue persisted for the given project URL, bypassing the
 * client. Used only as an OFFLINE fallback: when the network is down,
 * `getSession()` can return null for an expired-but-refreshable session
 * (refresh needs the network). The stored session — including its refresh
 * token — remains valid; gotrue auto-refreshes it once connectivity returns.
 */
export const readStoredSessionForUrl = (supabaseUrl: string): Session | null => {
  const key = getAuthStorageKeyForUrl(supabaseUrl);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session | null;
    if (parsed?.user?.id && parsed?.access_token && parsed?.refresh_token) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};
